import { AxiosError } from 'axios';
import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Scenario versions — Scenario Intelligence screens 05 (Quick review) and 10 (Advanced editor).
//
// A v2 scenario version travels as canonical JSON TEXT in both directions (`versionJson`, `currentJson`, `changeJson`):
// the shared client lowers the first letter of every response key, which would rename a tool's argument names inside a
// check. Parse it here, send it back as JSON.stringify — never let the client's casing pass touch it.
// Every rule is the server's (ScenarioApprovalRules, ScenarioVersionRules, ScenarioCompiler); this file only carries.
// ─────────────────────────────────────────────────────────────────────────────

// ── The v2 contract (OO.Business/AIInfo/ScenarioContract/EvaluationScenario.cs), as the server serializes it ──

export interface ScenarioCheckArgs {
  values?: string[];
  tool?: string;
  toolPattern?: string;
  withArgs?: Record<string, string>;
  responseContains?: string[];
  value?: string;
  sitsWith?: string[];
  neverWith?: string[];
  group?: number;
  enabled?: boolean;
  tolerance?: number;
  ignorePatterns?: string[];
}

export type ProtocolApplicability = 'Applies' | 'NeedsProtocol' | 'NotApplicable';

export interface ScenarioApplicability {
  withoutProtocol: ProtocolApplicability;
  reason?: string;
  ruleRef?: string;
}

export interface ScenarioCheck {
  id: string;
  kind: string;
  operator: string;
  scope: 'Turn' | 'Conversation';
  turnIndex?: number;
  /** Manual (contract version 3): an obligation a reviewer judges; no run marks it passed. */
  strength: 'Exact' | 'Weak' | 'Manual';
  args: ScenarioCheckArgs;
  label?: string;
  ruleIndex?: number;
  requirementRef?: string;
  applicability?: ScenarioApplicability;
}

export interface ScenarioEvent {
  id: string;
  kind: string;
  turnIndex: number;
  actor: string;
  text: string;
}

export interface ScenarioWorld {
  merchantId: number;
  memberId: number;
  memberContactId: number;
  infusionProtocolId: number;
  /** Contract version 3: a named world the run resolves to a seeded patient, instead of fixed ids. */
  role?: string;
}

/** The world's ids, as opposed to its version-3 role. */
export type WorldId = Exclude<keyof ScenarioWorld, 'role'>;

export interface ScenarioNote {
  at: string;
  text: string;
}

export interface ScenarioProvenance {
  origin?: string;
  sourcePath?: string;
  sourceHash?: string;
  compilerVersion?: string;
}

export interface EvaluationScenario {
  schemaVersion: number;
  scenarioId: string;
  purpose?: string;
  evaluationScope?: string;
  target: { agentId: number };
  world: ScenarioWorld;
  events: ScenarioEvent[];
  checks: ScenarioCheck[];
  provenance?: ScenarioProvenance;
  notes?: ScenarioNote[];
  // Contract version 3 (2026-11-02): optional, carried through every load and save.
  /** What goes wrong for the patient if the agent fails this case. */
  hazard?: string;
  /** What the expectations rest on: a URL or a source-register id, with its section and revision. */
  sources?: ScenarioSource[];
  /** What a tool answers in this scenario instead of running. */
  toolDoubles?: ScenarioToolDouble[];
  /** The scenario's siblings: links only, nothing a run executes. */
  relations?: ScenarioRelation[];
  /** Replies that prove the checks: a safe one they must pass, an unsafe one they must fail. */
  controls?: ScenarioControl[];
}

export interface ScenarioSource {
  ref: string;
  section?: string;
  revision?: string;
  note?: string;
}

export type ToolDoubleOutcome = 'Succeeds' | 'Fails' | 'TimesOut' | 'NotFiled';

export interface ScenarioToolDouble {
  tool: string;
  outcome: ToolDoubleOutcome;
  response: string;
  /** Unset: the double answers on every turn. */
  turnIndex?: number;
}

export type RelationKind = 'Paraphrase' | 'ContextChange' | 'Baseline';

export interface ScenarioRelation {
  kind: RelationKind;
  scenarioId: string;
  note?: string;
}

export type ControlKind = 'SafeParaphrase' | 'UnsafeMutation';

export interface ScenarioControl {
  id: string;
  kind: ControlKind;
  turnIndex: number;
  reply: string;
  toolCalls?: { tool: string; args?: Record<string, string>; response?: string }[];
  note?: string;
}

/** How one control behaves on the checks as written (ScenarioControlRules): a warning when it does not, never a block. */
export interface ControlVerdict {
  controlId: string;
  kind: ControlKind;
  turnIndex: number;
  behaves: boolean;
  observed: string;
}

/** The canonical form omits nulls; fill what the screens read so they never meet an undefined list or world. */
export function parseVersion(json: string | null | undefined): EvaluationScenario | null {
  if (!json) return null;
  const v = JSON.parse(json) as EvaluationScenario;
  return {
    ...v,
    world: {
      merchantId: v.world?.merchantId ?? 0,
      memberId: v.world?.memberId ?? 0,
      memberContactId: v.world?.memberContactId ?? 0,
      infusionProtocolId: v.world?.infusionProtocolId ?? 0,
      // Version 3's named world: kept, or a save from the editor would drop it.
      ...(v.world?.role ? { role: v.world.role } : {}),
    },
    events: v.events ?? [],
    checks: (v.checks ?? []).map((c) => ({ ...c, args: c.args ?? {} })),
  };
}

// ── What changed (ScenarioVersionDiff) ──

export type ScenarioChangePart =
  | 'Checks' | 'WhatTheUserSays' | 'Context' | 'Purpose' | 'Notes'
  // Contract version 3: the hazard or the sources (Basis), the tool doubles, the controls and the sibling links.
  | 'Basis' | 'ToolDoubles' | 'Controls' | 'Relations';

export interface ScenarioCheckChange {
  change: 'Added' | 'Removed' | 'Changed';
  before?: ScenarioCheck;
  after?: ScenarioCheck;
}

export interface ScenarioVersionChange {
  purposeBefore?: string;
  purposeAfter?: string;
  context: Array<{ part: string; before?: string; after?: string }>;
  events: Array<{ turnIndex: number; before?: string; after?: string }>;
  checks: ScenarioCheckChange[];
  notesChanged: boolean;
  basisChanged?: boolean;
  toolDoublesChanged?: boolean;
  controlsChanged?: boolean;
  relationsChanged?: boolean;
  purposeChanged: boolean;
  material: boolean;
  parts: ScenarioChangePart[];
}

// ── Review (ValidationReviewManager, screen 05) ──

export type ScenarioReviewPolicy = 'FirstReview' | 'Material' | 'WordingOnly';
export type ScenarioDecisionKind = 'Approved' | 'ChangesRequested' | 'Rejected' | 'Withdrawn';

export interface ScenarioVersionRef {
  snapshotId: number;
  number: number;
  bodyHash: string;
  schemaVersion: number;
  origin?: string | null;
  authorUserId: number;
  authorName?: string | null;
  savedAtUtc: string;
}

export interface ScenarioDecisionRef {
  id: number;
  snapshotId: number;
  versionNumber?: number | null;
  bodyHash?: string | null;
  decision: ScenarioDecisionKind;
  byUserId: number;
  byName?: string | null;
  role?: string | null;
  justification?: string | null;
  isSelfApproval: boolean;
  policy?: ScenarioReviewPolicy | null;
  batchId?: string | null;
  atUtc: string;
}

export interface ScenarioReviewItem {
  scenarioId: string;
  title?: string | null;
  category?: string | null;
  isDatabaseManaged: boolean;
  current: ScenarioVersionRef;
  comparedWith?: ScenarioVersionRef | null;
  comparedWithIsApproved: boolean;
  /** The newest decision on the current version; null while it is open. */
  decision?: ScenarioDecisionKind | null;
  decisionRow?: ScenarioDecisionRef | null;
  policy: ScenarioReviewPolicy;
  batchAllowed: boolean;
  changedParts: ScenarioChangePart[];
  blockers: string[];
  authoredByViewer: boolean;
  decidedByViewer: boolean;
}

export interface ScenarioReviewQueue {
  agentId: number;
  viewerUserId: number;
  items: ScenarioReviewItem[];
}

export interface ScenarioReviewCase {
  item: ScenarioReviewItem;
  current: EvaluationScenario | null;
  compared: EvaluationScenario | null;
  change: ScenarioVersionChange | null;
  versions: ScenarioVersionRef[];
  decisions: ScenarioDecisionRef[];
  /** How the current version's controls behave on its checks; empty when it has none. */
  controls: ControlVerdict[];
}

interface ScenarioReviewCaseWire {
  item: ScenarioReviewItem;
  currentJson?: string | null;
  comparedJson?: string | null;
  changeJson?: string | null;
  versions: ScenarioVersionRef[];
  decisions: ScenarioDecisionRef[];
  controls?: ControlVerdict[] | null;
}

async function unwrap<T>(p: Promise<OperationResult<T>>, fallback: string): Promise<T> {
  const wrapped = await p;
  if (!wrapped.isSuccess || wrapped.data == null) throw new Error(wrapped.message || fallback);
  return wrapped.data;
}

/**
 * World seeder W5: a world a scenario can name, the protocol template it is set up on, and the agents that template serves.
 * A run refuses a world for any other agent, so the editor offers an agent its own therapy's worlds.
 */
export interface EvaluationWorldEntry {
  role: string;
  version: number;
  /** The world in words, e.g. "FOLFOX, cycle 2, day 10". */
  words: string;
  templateId: number;
  templateName?: string | null;
  cycle: number;
  day: number;
  agentIds: number[];
}

export const listEvaluationWorlds = (signal?: AbortSignal) =>
  unwrap(apiGet<OperationResult<EvaluationWorldEntry[]>>('/api/Scenarios/Worlds', undefined, { signal }), 'Could not load the evaluation worlds');

export const getScenarioReviewQueue = (agentId: number, signal?: AbortSignal) =>
  unwrap(apiGet<OperationResult<ScenarioReviewQueue>>(`/api/ValidationRuns/Agents/${agentId}/Scenarios/Reviews`, undefined, { signal }),
    'Could not load the review queue');

export async function getScenarioReview(agentId: number, scenarioId: string, signal?: AbortSignal): Promise<ScenarioReviewCase> {
  const wire = await unwrap(
    apiGet<OperationResult<ScenarioReviewCaseWire>>(`/api/ValidationRuns/Agents/${agentId}/Scenarios/Review`, { scenarioId }, { signal }),
    'Could not load this scenario for review',
  );
  return {
    item: wire.item,
    current: parseVersion(wire.currentJson),
    compared: parseVersion(wire.comparedJson),
    change: wire.changeJson ? (JSON.parse(wire.changeJson) as ScenarioVersionChange) : null,
    versions: wire.versions ?? [],
    decisions: wire.decisions ?? [],
    controls: wire.controls ?? [],
  };
}

export interface ScenarioDecisionInput {
  scenarioId: string;
  scenarioSnapshotId: number;
  scenarioBodyHash: string;
  decision: ScenarioDecisionKind;
  reviewerRole: string;
  justification: string;
}

export const decideScenarioVersion = (agentId: number, body: ScenarioDecisionInput) =>
  unwrap(apiPost<OperationResult<{ id: number; isSelfApproval: boolean }>>(`/api/ValidationRuns/Agents/${agentId}/Scenarios/Approval`, body),
    'Could not record the decision');

export interface BatchItem {
  scenarioId: string;
  snapshotId: number;
  bodyHash: string;
}

export interface BatchExclusion {
  scenarioId: string;
  snapshotId: number;
  reason: string;
}

export interface BatchOutcome {
  batchId?: string | null;
  recorded: Array<{ id: number; scenarioId: string; scenarioSnapshotId: number; scenarioBodyHash: string; isSelfApproval: boolean }>;
  excluded: BatchExclusion[];
}

/** Which of the ticked versions a batch would leave out now, with the server's reason for each. Writes nothing. */
export const previewScenarioBatch = (agentId: number, items: BatchItem[]) =>
  unwrap(apiPost<OperationResult<{ excluded: BatchExclusion[]; eligible: BatchItem[] }>>(
    `/api/ValidationRuns/Agents/${agentId}/Scenarios/Approval/Batch/Preview`, { items }), 'Could not check the selection');

/** Thrown when the server recorded nothing because some confirmed versions cannot be approved together now. */
export class BatchRefusedError extends Error {
  constructor(message: string, readonly excluded: BatchExclusion[]) {
    super(message);
    this.name = 'BatchRefusedError';
  }
}

export async function approveScenarioBatch(
  agentId: number,
  body: { items: BatchItem[]; reviewerRole: string; justification: string },
): Promise<BatchOutcome> {
  const wrapped = await apiPost<OperationResult<BatchOutcome>>(`/api/ValidationRuns/Agents/${agentId}/Scenarios/Approval/Batch`, body);
  if (!wrapped.isSuccess) throw new BatchRefusedError(wrapped.message || 'Nothing was recorded.', wrapped.data?.excluded ?? []);
  return wrapped.data!;
}

// ── The advanced editor (ScenarioAuthoringManager, screen 10) ──

export interface ScenarioVersionCheck {
  problems: string[];
  notApplicableCheckIds: string[];
  evaluationScope?: string | null;
  hash?: string | null;
  runnable: boolean;
  changedParts: ScenarioChangePart[];
  /** How each control behaves on the checks as written; null when the version does not compile. */
  controls?: ControlVerdict[] | null;
}

export interface ScenarioVersionForEdit {
  scenarioId: string;
  agentId: number;
  agentName?: string | null;
  domain?: string | null;
  title?: string | null;
  category?: string | null;
  version: EvaluationScenario | null;
  snapshotId: number;
  baseHash: string;
  storedSchemaVersion: number;
  isDatabaseManaged: boolean;
  editable: boolean;
  notEditableReason?: string | null;
  check?: ScenarioVersionCheck | null;
}

export async function getScenarioVersion(scenarioId: string, signal?: AbortSignal): Promise<ScenarioVersionForEdit> {
  const wire = await unwrap(
    apiGet<OperationResult<Omit<ScenarioVersionForEdit, 'version'> & { versionJson?: string | null }>>('/api/Scenarios/Version', { scenarioId }, { signal }),
    'Could not open this scenario',
  );
  const { versionJson, ...rest } = wire;
  return { ...rest, version: parseVersion(versionJson) };
}

export const checkScenarioVersion = (version: EvaluationScenario, signal?: AbortSignal) =>
  unwrap(apiPost<OperationResult<ScenarioVersionCheck>>('/api/Scenarios/Version/Check', { versionJson: JSON.stringify(version) }, { signal }),
    'Could not check this version');

export type SaveOutcome = 'Saved' | 'Unchanged' | 'Refused' | 'Conflict' | 'NotFound' | 'NotEditable';

export interface ScenarioVersionSave {
  outcome: SaveOutcome;
  problems: string[];
  snapshotId: number;
  hash?: string | null;
  /** Conflict: the version someone else saved, for the three-way merge. */
  current?: EvaluationScenario | null;
  currentSavedBy?: number;
  currentSavedAtUtc?: string | null;
}

interface SaveWire {
  outcome: SaveOutcome;
  problems?: string[];
  snapshotId?: number;
  hash?: string | null;
  currentJson?: string | null;
  currentSavedBy?: number;
  currentSavedAtUtc?: string | null;
}

/**
 * Saves an edited version opened at `baseHash`. Never throws for an answer the server gave: a refusal, a conflict (HTTP
 * 409, carrying the newer version) and a not-found come back as outcomes for the drawer to show.
 */
export async function saveScenarioVersion(version: EvaluationScenario, baseHash: string): Promise<ScenarioVersionSave> {
  let wrapped: OperationResult<SaveWire>;
  try {
    wrapped = await apiPost<OperationResult<SaveWire>>('/api/Scenarios/Version', { versionJson: JSON.stringify(version), baseHash });
  } catch (e) {
    const body = (e as AxiosError<OperationResult<SaveWire>>).response?.data;
    if (!body?.data?.outcome) throw e;
    wrapped = body;
  }
  if (!wrapped.data) throw new Error(wrapped.message || 'Could not save this version');
  const d = wrapped.data;
  return {
    outcome: d.outcome,
    problems: d.problems ?? [],
    snapshotId: d.snapshotId ?? 0,
    hash: d.hash,
    current: parseVersion(d.currentJson),
    currentSavedBy: d.currentSavedBy,
    currentSavedAtUtc: d.currentSavedAtUtc,
  };
}

export interface StandaloneVariantResult {
  problems: string[];
  newScenarioId?: string | null;
  keptCheckIds: string[];
  droppedCheckIds: string[];
  snapshotId: number;
  hash?: string | null;
  created: boolean;
}

export async function standaloneVariant(version: EvaluationScenario, baseHash: string, preview: boolean): Promise<StandaloneVariantResult> {
  const wrapped = await apiPost<OperationResult<StandaloneVariantResult>>('/api/Scenarios/StandaloneVariant',
    { versionJson: JSON.stringify(version), baseHash, preview });
  if (!wrapped.data) throw new Error(wrapped.message || 'Could not make the standalone variant');
  return { ...wrapped.data, problems: wrapped.data.problems ?? [] };
}
